import { useEffect, useMemo, useState } from 'react';
import { attendanceAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import { CalendarDays, UserRound, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  present: 'bg-emerald-100 text-emerald-700',
  late: 'bg-amber-100 text-amber-700',
  half_day: 'bg-orange-100 text-orange-700',
  partial: 'bg-violet-100 text-violet-700',
  off_schedule: 'bg-purple-100 text-purple-700',
  on_leave: 'bg-sky-100 text-sky-700',
  absent: 'bg-rose-100 text-rose-700',
  holiday: 'bg-blue-100 text-blue-700',
  not_scheduled: 'bg-slate-100 text-slate-700',
};

const ParentPortal = () => {
  const { terminology, entityCategory } = useAuth();
  const { people, linkedPerson } = terminology;
  const [children, setChildren] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const dateValue = new Date();
    dateValue.setDate(1);
    return dateValue.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    attendanceAPI.getParentChildren()
      .then((response) => {
        const loadedChildren = response.data.children || [];
        setChildren(loadedChildren);
        if (loadedChildren.length > 0) {
          setSelectedStudentId(String(loadedChildren[0].id));
        }
      })
      .catch((err) => toast.error(err.response?.data?.error || `Failed to load linked ${people.toLowerCase()}`))
      .finally(() => setLoadingChildren(false));
  }, [people]);

  useEffect(() => {
    if (!selectedStudentId || !fromDate || !toDate) {
      setReport(null);
      return;
    }

    setLoadingReport(true);
    attendanceAPI.getParentChildReport({
      student_id: selectedStudentId,
      from_date: fromDate,
      to_date: toDate,
    })
      .then((response) => setReport(response.data))
      .catch((err) => toast.error(err.response?.data?.error || `Failed to load ${linkedPerson.toLowerCase()} attendance`))
      .finally(() => setLoadingReport(false));
  }, [selectedStudentId, fromDate, toDate, linkedPerson]);

  const selectedChild = useMemo(
    () => children.find((child) => String(child.id) === String(selectedStudentId)) || null,
    [children, selectedStudentId]
  );

  const summaryCards = useMemo(() => {
    if (!report?.summary) {
      return [];
    }

    return [
      { label: 'Scheduled Days', value: report.summary.total_expected_days, tone: 'text-slate-900' },
      { label: 'Attended', value: report.summary.attended_days, tone: 'text-emerald-700' },
      { label: 'Late', value: report.summary.late, tone: 'text-amber-700' },
      { label: 'Partial', value: report.summary.partial, tone: 'text-violet-700' },
      { label: 'On Leave', value: report.summary.on_leave, tone: 'text-sky-700' },
      { label: 'Absent', value: report.summary.absent, tone: 'text-rose-700' },
      { label: 'Attendance %', value: `${report.summary.attendance_percentage}%`, tone: 'text-teal-700' },
    ];
  }, [report]);

  return (
    <div className="space-y-6">
      <div className="app-hero rounded-[2rem] px-6 py-6 md:px-8">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="app-kicker">{terminology.roleLabels.parent} Access</p>
            <h1 className="mt-2 text-3xl font-semibold">Track {entityCategory === 'school' ? 'your child’s' : 'linked people’s'} attendance in one place</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/75">
              View linked {terminology.people.toLowerCase()}, select one, and check attendance history, attendance percentage, and daily status over any period.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <UserRound size={14} />
                {terminology.linkedPerson}
              </span>
              <select
                value={selectedStudentId}
                onChange={(event) => setSelectedStudentId(event.target.value)}
                disabled={loadingChildren || children.length === 0}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              >
                <option value="" className="text-slate-900">Select {terminology.linkedPerson.toLowerCase()}</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id} className="text-slate-900">
                    {child.full_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <CalendarDays size={14} />
                From
              </span>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              />
            </label>

            <label className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/80">
              <span className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/60">
                <CalendarDays size={14} />
                To
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-white outline-none"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="app-surface rounded-[1.75rem] p-6">
        <div className="mb-4 flex items-center gap-2">
          <Users size={18} className="text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900">Linked children</h2>
        </div>

        {loadingChildren ? (
          <div className="py-12 text-center text-slate-400">Loading linked children...</div>
        ) : children.length === 0 ? (
          <div className="py-12 text-center text-slate-400">No children are linked to this account yet.</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {children.map((child) => (
              <button
                key={child.id}
                type="button"
                onClick={() => setSelectedStudentId(String(child.id))}
                className={`rounded-[1.5rem] border p-5 text-left transition-all ${
                  String(selectedStudentId) === String(child.id)
                    ? 'border-teal-400 bg-teal-50/70 shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-slate-900">{child.full_name}</p>
                    <p className="mt-1 text-sm text-slate-500">{child.section_name || 'No section assigned'}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColors[child.latest_status || 'not_scheduled'] || statusColors.not_scheduled}`}>
                    {(child.latest_status || 'not_scheduled').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="mt-4 space-y-1 text-sm text-slate-500">
                  <p>ID: {child.employee_id || '—'}</p>
                  <p>Department: {child.department_name || '—'}</p>
                  <p>Latest location: {child.latest_location_name || 'No scan today'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedChild && (
        <div className="app-surface rounded-[1.75rem] p-6">
          <h2 className="text-2xl font-semibold text-slate-900">{selectedChild.full_name}</h2>
          <p className="mt-2 text-sm text-slate-500">
            {selectedChild.department_name || 'No department'} • {selectedChild.section_name || `No ${terminology.group.toLowerCase()}`}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {formatDate(`${fromDate}T00:00:00`)} to {formatDate(`${toDate}T00:00:00`)}
          </p>
        </div>
      )}

      {selectedStudentId && summaryCards.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
          {summaryCards.map((card) => (
            <div key={card.label} className="app-surface rounded-[1.5rem] p-5">
              <p className="text-sm text-slate-500">{card.label}</p>
              <p className={`mt-2 text-3xl font-semibold ${card.tone}`}>{card.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="app-surface rounded-[1.75rem] p-6">
        <div className="mb-4 flex items-center gap-2">
          <UserRound size={18} className="text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900">Daily attendance history</h2>
        </div>

        {!selectedStudentId ? (
          <div className="py-16 text-center text-slate-400">Select a child to view attendance history.</div>
        ) : loadingReport ? (
          <div className="py-16 text-center text-slate-400">Loading attendance history...</div>
        ) : !report?.records?.length ? (
          <div className="py-16 text-center text-slate-400">No attendance records found for this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200/70 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left">Date</th>
                  <th className="px-3 py-3 text-left">Day</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">Periods</th>
                  <th className="px-3 py-3 text-left">{terminology.area}</th>
                  <th className="px-3 py-3 text-left">{terminology.leader}</th>
                  <th className="px-3 py-3 text-left">Location</th>
                  <th className="px-3 py-3 text-left">Scan</th>
                </tr>
              </thead>
              <tbody>
                {report.records.map((record) => (
                  <tr key={record.date} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-4 text-slate-700">{formatDate(`${record.date}T00:00:00`)}</td>
                    <td className="px-3 py-4 text-slate-600">{record.day_name}</td>
                    <td className="px-3 py-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColors[record.status] || 'bg-slate-100 text-slate-700'}`}>
                        {record.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-slate-600">{record.attended_periods}/{record.scheduled_periods}</td>
                    <td className="px-3 py-4 text-slate-600">{record.subject_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{record.teacher_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">{record.location_name || '—'}</td>
                    <td className="px-3 py-4 text-slate-600">
                      {record.scan_time
                        ? new Date(record.scan_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : 'No scan'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentPortal;
